
import React, { useState } from 'react';
import { Activity, Mail, Lock, User, ArrowRight, ShieldCheck, Loader2, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';

interface LoginViewProps {
  onLogin: (email: string) => void;
  onBack?: () => void;
}

const LoginView: React.FC<LoginViewProps> = ({ onLogin, onBack }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const currentYear = new Date().getFullYear();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: name }
          }
        });
        if (error) throw error;

        if (data.user) {
          await supabase.from('profiles').insert([{ id: data.user.id, name, role: 'USER', active: true }]);
        }
        alert('Conta criada com sucesso! Verifique seu email ou tente acessar.');
        setIsLogin(true);
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Ocorreu um erro inesperado.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0e11] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#00ff66]/5 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#00ff66]/5 rounded-full blur-[120px]"></div>

      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-8 left-8 flex items-center gap-2 text-[#848e9c] hover:text-[#00ff66] transition-colors font-black uppercase text-[10px] tracking-widest"
        >
          <ChevronLeft size={16} /> Voltar para o Início
        </button>
      )}

      <div className="w-full max-w-md z-10">
        <div className="flex flex-col items-center mb-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="bg-[#00ff66] p-1.5 rounded-lg shadow-[0_0_25px_rgba(0,255,102,0.4)] mb-4">
            <Activity className="text-black" size={32} strokeWidth={3} />
          </div>
          <h1 className="text-4xl font-black tracking-tighter text-white uppercase">
            FIFA<span className="text-[#00ff66]">LYTICS</span>
          </h1>
          <p className="text-[#848e9c] text-[9px] font-black uppercase tracking-[0.3em] mt-2 opacity-50">Professional E-Soccer Suite</p>
        </div>

        <div className="bg-[#1e2329] p-8 rounded-[32px] border border-[#474d57] shadow-2xl animate-in zoom-in-95 duration-500">
          <div className="flex bg-[#0b0e11] p-1 rounded-2xl mb-8">
            <button
              onClick={() => { setIsLogin(true); setErrorMsg(''); }}
              className={`flex-1 py-3 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${isLogin ? 'bg-[#2b3139] text-[#00ff66] shadow-lg' : 'text-[#848e9c] hover:text-[#eaecef]'}`}
            >
              Entrar
            </button>
            <button
              onClick={() => { setIsLogin(false); setErrorMsg(''); }}
              className={`flex-1 py-3 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${!isLogin ? 'bg-[#2b3139] text-[#00ff66] shadow-lg' : 'text-[#848e9c] hover:text-[#eaecef]'}`}
            >
              Criar Conta
            </button>
          </div>

          {errorMsg && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold p-4 rounded-2xl mb-6 text-center">
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <label className="text-[10px] font-black text-[#848e9c] uppercase tracking-widest ml-1">Nome Completo</label>
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[#848e9c] group-focus-within:text-[#00ff66] transition-colors" size={18} />
                  <input
                    type="text"
                    required={!isLogin}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome de trader"
                    className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:border-[#00ff66] outline-none transition-all placeholder:text-[#474d57]"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-black text-[#848e9c] uppercase tracking-widest ml-1">E-mail</label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#848e9c] group-focus-within:text-[#00ff66] transition-colors" size={18} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:border-[#00ff66] outline-none transition-all placeholder:text-[#474d57]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <label className="text-[10px] font-black text-[#848e9c] uppercase tracking-widest">Senha</label>
                {isLogin && (
                  <button type="button" className="text-[10px] font-bold text-[#00ff66] hover:underline uppercase tracking-widest">Esqueci a senha</button>
                )}
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#848e9c] group-focus-within:text-[#00ff66] transition-colors" size={18} />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#0b0e11] border border-[#474d57] rounded-2xl pl-12 pr-4 py-4 text-sm font-bold focus:border-[#00ff66] outline-none transition-all placeholder:text-[#474d57]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#00ff66] text-[#0b0e11] font-black py-5 rounded-2xl text-sm flex items-center justify-center gap-2 hover:shadow-[0_8px_30px_rgba(0,255,102,0.3)] transition-all transform active:scale-[0.98] uppercase tracking-widest mt-8 disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="animate-spin" size={20} /> : (isLogin ? 'Acessar' : 'Registrar Agora')}
              {!isLoading && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-[#474d57]/30 text-center">
            <p className="text-xs text-[#848e9c] font-medium flex items-center justify-center gap-2">
              <ShieldCheck size={14} className="text-[#00ff66]" fill="none" />
              Acesso seguro e criptografado
            </p>
          </div>
        </div>

        <div className="mt-8 text-center px-4">
          <p className="text-[#00ff66] text-[10px] font-black uppercase tracking-[0.4em]">
            © {currentYear} FIFALYTICS PRO • IDEALIZADO PELO DUPLA FIFA SOLUTIONS
          </p>
          <p className="text-[#b1b5c0] text-[8px] font-bold uppercase tracking-[0.2em] mt-4 max-w-sm mx-auto opacity-70">
            Apostas esportivas envolvem risco no FIFA Online. Opere com responsabilidade. Esta ferramenta é para fins analíticos e não garante lucros financeiros.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
